import { AssetsService } from '@coosmo/shared-utils'

const fullPageIcon = AssetsService.getSvgIcon('full-screen.svg')
const fullPageExitIcon = AssetsService.getSvgIcon('full-screen-exit.svg')

export const VIEWER_FULLSCREEN_CHANGE_EVENT = 'nexus-viewer-fullscreenchange'

const nativeFullscreenEvents = ['fullscreenchange', 'webkitfullscreenchange']
const supportsNativeFullscreen = !!(
  document.documentElement.requestFullscreen ||
  document.documentElement.webkitRequestFullscreen
)

let isCssFullscreen = false
let previousTargetInlineStyle = ''
let previousBodyOverflow = ''
let fullscreenListenersBound = false
let escapeListenerBound = false
let fullscreenBehavior = 'native'

function isIOSLikeDevice() {
  const userAgent = navigator.userAgent || ''
  return (
    /iPad|iPhone|iPod/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

function shouldUseControlledFullscreen(currentMode) {
  return currentMode === 'freeroam' && isIOSLikeDevice()
}

function getCssFullscreenTarget() {
  return document.getElementById('widgetContainer') || document.documentElement
}

function getNativeFullscreenTarget() {
  return document.documentElement
}

function getNativeFullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null
}

function emitViewerFullscreenChange() {
  const isFullscreen = isViewerFullscreenActive()
  updateIcon(isFullscreen)
  window.dispatchEvent(
    new CustomEvent(VIEWER_FULLSCREEN_CHANGE_EVENT, {
      detail: {
        isFullscreen,
        mode: isCssFullscreen ? 'css' : getNativeFullscreenElement() ? 'native' : 'none',
      },
    })
  )
}

function ensureFullscreenListeners() {
  if (fullscreenListenersBound) return

  nativeFullscreenEvents.forEach(eventName => {
    document.addEventListener(eventName, emitViewerFullscreenChange)
  })
  fullscreenListenersBound = true
}

function ensureEscapeListener() {
  if (escapeListenerBound) return

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return
    if (!isCssFullscreen) return
    if (fullscreenBehavior !== 'controlled') return
    if (document.pointerLockElement) return

    event.preventDefault()
    exitViewerFullscreen()
  })

  escapeListenerBound = true
}

function enterCssFullscreen(elem) {
  if (!elem) return

  previousTargetInlineStyle = elem.getAttribute('style') || ''
  previousBodyOverflow = document.body.style.overflow || ''

  elem.style.setProperty('position', 'fixed', 'important')
  elem.style.setProperty('inset', '0', 'important')
  elem.style.setProperty('width', '100vw', 'important')
  elem.style.setProperty('height', '100dvh', 'important')
  elem.style.setProperty('z-index', '2147483647', 'important')
  elem.style.setProperty('background', '#000', 'important')
  document.body.style.overflow = 'hidden'

  isCssFullscreen = true
  window.dispatchEvent(new Event('resize'))
  emitViewerFullscreenChange()
}

function exitCssFullscreen(elem) {
  if (!elem || !isCssFullscreen) return

  if (previousTargetInlineStyle) {
    elem.setAttribute('style', previousTargetInlineStyle)
  } else {
    elem.removeAttribute('style')
  }

  document.body.style.overflow = previousBodyOverflow
  isCssFullscreen = false
  window.dispatchEvent(new Event('resize'))
  emitViewerFullscreenChange()
}

function updateIcon(isFullscreen) {
  const img = document.querySelector('#full-page > img')
  if (img) img.src = isFullscreen ? fullPageExitIcon : fullPageIcon
}

function callFullscreenMethod(target, method, onError) {
  if (!method) return

  try {
    const result = method.call(target)
    if (result && typeof result.catch === 'function') {
      result.catch(onError)
    }
  } catch (err) {
    onError(err)
  }
}

function openStandaloneWindow() {
  return window.open(window.location.href, '_blank', 'noopener,noreferrer')
}

function applyFullscreenFallback({ allowWindowFallback, allowCssFallback, elem }) {
  if (allowWindowFallback && isIOSLikeDevice() && window.self !== window.top) {
    const openedWindow = openStandaloneWindow()
    if (openedWindow) {
      return true
    }
  }

  if (allowCssFallback) {
    enterCssFullscreen(elem)
    return true
  }

  return false
}

export function isViewerFullscreenActive() {
  return Boolean(getNativeFullscreenElement() || isCssFullscreen)
}

export function requestViewerFullscreen(options = {}) {
  const {
    allowWindowFallback = false,
    allowCssFallback = true,
    fallbackDelayMs = 200,
    suppressFailureLogs = false,
    preferFallbackOnly = false,
  } = options

  ensureFullscreenListeners()

  const cssTarget = getCssFullscreenTarget()
  const nativeTarget = getNativeFullscreenTarget()
  if ((!cssTarget && !nativeTarget) || isViewerFullscreenActive()) return

  let fallbackApplied = false
  const applyFallbackOnce = () => {
    if (fallbackApplied || isViewerFullscreenActive()) return
    fallbackApplied = applyFullscreenFallback({
      allowWindowFallback,
      allowCssFallback,
      elem: cssTarget,
    })
  }

  if (preferFallbackOnly || !supportsNativeFullscreen) {
    applyFallbackOnce()
    return
  }

  const requestMethod = nativeTarget?.requestFullscreen || nativeTarget?.webkitRequestFullscreen
  callFullscreenMethod(nativeTarget, requestMethod, err => {
    if (!suppressFailureLogs) {
      console.warn('requestFullscreen failed, falling back to workaround:', err)
    }
    applyFallbackOnce()
  })

  window.setTimeout(() => {
    if (!isViewerFullscreenActive()) {
      applyFallbackOnce()
    }
  }, fallbackDelayMs)
}

export function exitViewerFullscreen() {
  ensureFullscreenListeners()

  const cssTarget = getCssFullscreenTarget()
  if (!cssTarget || !isViewerFullscreenActive()) return

  if (isCssFullscreen) {
    exitCssFullscreen(cssTarget)
    return
  }

  const exitMethod = document.exitFullscreen || document.webkitExitFullscreen
  callFullscreenMethod(document, exitMethod, err => {
    console.warn('exitFullscreen failed:', err)
  })
}

function toggleViewerFullscreen() {
  if (isViewerFullscreenActive()) {
    exitViewerFullscreen()
    return
  }

  if (fullscreenBehavior === 'controlled') {
    requestViewerFullscreen({
      allowWindowFallback: isIOSLikeDevice(),
      allowCssFallback: true,
      preferFallbackOnly: true,
      suppressFailureLogs: true,
    })
    return
  }

  requestViewerFullscreen({
    allowWindowFallback: isIOSLikeDevice(),
    allowCssFallback: true,
    suppressFailureLogs: isIOSLikeDevice(),
  })
}

export function setupFullscreenButton(options = {}) {
  const { currentMode = 'orbit' } = options

  ensureFullscreenListeners()
  ensureEscapeListener()
  fullscreenBehavior = shouldUseControlledFullscreen(currentMode) ? 'controlled' : 'native'

  const fullScreenButton = document.getElementById('full-page')
  if (!fullScreenButton) return

  if (!fullScreenButton.dataset.bound) {
    fullScreenButton.addEventListener('click', () => {
      toggleViewerFullscreen()
    })
    fullScreenButton.dataset.bound = 'true'
  }

  emitViewerFullscreenChange()
}
